<script>
import { mapState } from "vuex";
import { get } from "lodash";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import ForwardButton from "@/components/ForwardButton";
import BackwardButton from "@/components/BackwardButton";

const MESSAGE_BY_STATUS = {
  400: "Não foi possível enviar o formulário.",
  404: "Não encontramos a indicação que você está procurando.",
  406: "Sua indicação já foi confirmada.",
  409: "Você já sugeriu essa indicação.",
}

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    ForwardButton,
    BackwardButton,
  },
  data: () => ({
    radio: "0",
    showInputs: false,
    name: "",
    email: "",
    alreadyReferral: false,
  }),
  computed: {
    ...mapState("company", ["company"]),
    color() {
      return this.company.base_hexcolor || "#1551b1";
    },
    isAnonimous() {
      const label = get(this.$router, "history.current.params.company_label", null);
      return label === "anonima";
    },
    vacancyId() {
      return get(this.$router, "history.current.params.vacancy_id", null);
    },
  },
  watch: {
    radio(value) {
      if (value === "1") {
        this.showInputs = true;
      } else {
        this.showInputs = false;
      }
    },
  },
  methods: {
    async handleBackward() {
      await this.$store.dispatch("step/decrementCurrentStep");

      const { path } = this.$store.getters["step/current"];
      const { params } = this.$router.history.current;

      this.$router.push({
        name: path,
        params,
      });
    },
    async handleForward() {
      try {
        const { params } = this.$router.history.current;

        if (this.radio === "0" || this.alreadyReferral || !this.vacancyId) {
          await this.$store.dispatch("step/completeStep");
          await this.$store.dispatch("step/incrementCurrentStep");

          const { path } = this.$store.getters["step/current"];

          this.$router.push({
            name: path,
            params,
          });
        } else {
          if (this.showInputs) {
            const isValid = await this.$refs.observer.validate();
            if (!isValid) {
              return;
            }
          }
          this.$vs.loading({ text: "Enviando..." });

          this.$http.post("/api/v1/indications?debug=true", {
            vacancy_id: this.vacancyId,
            name: this.name,
            email: this.email,
          })
            .then(async () => {
              sessionStorage.setItem("@recrutei:talent:referral_name", this.name);
              sessionStorage.setItem("@recrutei:talent:referral_email", this.email);

              this.$vs.loading.close();

              // PostHog: referral_created
              try {
                const { trackEvent } = await import("@/services/posthog");
                trackEvent("referral_created", {
                  referrer_name: this.name,
                  referrer_email: this.email,
                  ...(this.vacancyId && { vacancy_id: this.vacancyId }),
                });
              } catch (_) {}

              await this.$store.dispatch("step/completeStep");
              await this.$store.dispatch("step/incrementCurrentStep");

              const { path } = this.$store.getters["step/current"];

              this.$vs.notify({
                text: "A indicação foi solicitada com sucesso!",
                title: "Sucesso!",
                icon: "icon-alert-circle",
                color: "success",
                iconPack: "feather",
                position: "top-right",
                time: 5000,
              });

              this.$router.push({
                name: path,
                params,
              });
            })
            .catch((error) => {
              const status = get(error, "response.status", 0);
              const message = MESSAGE_BY_STATUS[status];

              this.$vs.notify({
                text: message,
                title: "Opps!",
                icon: "icon-alert-circle",
                color: "warning",
                iconPack: "feather",
                position: "top-right",
                time: 5000,
              });

              this.$vs.loading.close();
            });
        }
      } catch (error) {
        this.$vs.loading.close();

        const message = get(
          error,
          "response.data.message",
          "Não foi possível enviar o formulário"
        );

        this.$vs.notify({
          text: message,
          title: "Opps!",
          icon: "icon-alert-circle",
          color: "warning",
          iconPack: "feather",
          position: "top-right",
          time: 5000,
        });
      }
    },
  },
  created() {
    const name = sessionStorage.getItem("@recrutei:talent:referral_name");
    const email = sessionStorage.getItem("@recrutei:talent:referral_email");

    if (name && email) {
      this.name = name;
      this.email = email;
      this.radio = "1";
      this.alreadyReferral = true;
    }
  },
};
</script>

<template>
  <div class="w-full flex flex-col items-center justify-center">
    <div class="w-full h-12 hidden sm:flex"></div>
    <div class="card flex flex-col bg-white rounded max-w-sm w-full">
      <div class="flex flex-col w-full space-y-2">
        <button class="nxp-back-btn hidden sm:flex" @click="handleBackward">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <mask id="mask0_857_10485" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="16"
              height="16">
              <rect width="16" height="16" fill="#696969" />
            </mask>
            <g mask="url(#mask0_857_10485)">
              <path
                d="M9.33362 11.7688L5.56445 7.99964L9.33362 4.23047L10.0361 4.93297L6.96945 7.99964L10.0361 11.0663L9.33362 11.7688Z"
                fill="#A9A9A9" />
            </g>
          </svg>
          Voltar
        </button>
        <span v-if="jobPositionReference" class="text-secondary-body text-[14px] font-normal leading-normal font-body">
          {{ jobPositionReference }}
        </span>
        <h1 class="nxp-title-form">
          Informe quem te indicou
        </h1>
        <span class="nxp-subtitle-form">
          Caso tenha sido indicado, informe aqui o nome e o e-mail de quem te indicou.
        </span>
      </div>

      <div class="nxp-referal-card-info my-8">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M11.25 16.75H12.75V11H11.25V16.75ZM12 9.2885C12.2288 9.2885 12.4207 9.21108 12.5755 9.05625C12.7303 8.90142 12.8077 8.70958 12.8077 8.48075C12.8077 8.25192 12.7303 8.06008 12.5755 7.90525C12.4207 7.75058 12.2288 7.67325 12 7.67325C11.7712 7.67325 11.5793 7.75058 11.4245 7.90525C11.2697 8.06008 11.1923 8.25192 11.1923 8.48075C11.1923 8.70958 11.2697 8.90142 11.4245 9.05625C11.5793 9.21108 11.7712 9.2885 12 9.2885ZM12.0017 21.5C10.6877 21.5 9.45267 21.2507 8.2965 20.752C7.14033 20.2533 6.13467 19.5766 5.2795 18.7218C4.42433 17.8669 3.74725 16.8617 3.24825 15.706C2.74942 14.5503 2.5 13.3156 2.5 12.0017C2.5 10.6877 2.74933 9.45267 3.248 8.2965C3.74667 7.14033 4.42342 6.13467 5.27825 5.2795C6.13308 4.42433 7.13833 3.74725 8.294 3.24825C9.44967 2.74942 10.6844 2.5 11.9983 2.5C13.3123 2.5 14.5473 2.74933 15.7035 3.248C16.8597 3.74667 17.8653 4.42342 18.7205 5.27825C19.5757 6.13308 20.2528 7.13833 20.7518 8.294C21.2506 9.44967 21.5 10.6844 21.5 11.9983C21.5 13.3123 21.2507 14.5473 20.752 15.7035C20.2533 16.8597 19.5766 17.8653 18.7218 18.7205C17.8669 19.5757 16.8617 20.2528 15.706 20.7518C14.5503 21.2506 13.3156 21.5 12.0017 21.5ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z"
            fill="#FF5900" />
        </svg>
        <div>
          <h4>
            Este espaço é exclusivo para programas de indicação, não para referências
          </h4>
          <p>
            Informe a indicação somente se um(a) colaborador(a) indicou você, pois um e-mail será enviado a essa pessoa
            para confirmar a indicação
          </p>
        </div>
      </div>


      <div class="flex flex-col space-y-2 mt-4">
        <p class="nxp-label">
          Você foi indicado para esta vaga?
          <span> - Opcional</span>
        </p>
        <div class="flex flex-col space-y-2">
          <vs-radio class="nxp-checkbox-container" v-model="radio" vs-value="1" :disabled="isAnonimous || !vacancyId">Sim</vs-radio>
          <vs-radio class="nxp-checkbox-container" v-model="radio" vs-value="0" :disabled="isAnonimous || !vacancyId">Não</vs-radio>
        </div>
      </div>

      <p class="anonimous__msg" v-if="isAnonimous">Não é possível fazer uma indicação em uma vaga anônima!</p>
      <p class="anonimous__msg" v-if="!vacancyId">Não é possível fazer uma indicação ao se cadastrar na base de talentos!</p>

      <div v-if="showInputs" class="flex flex-col space-y-6 mt-8">
        
        <hr class="custom-hr">

        <span class="nxp-subtitle-form">
          Insira o nome e o e-mail de quem te indicou.
          Enviaremos um e-mail de confirmação para esta pessoa.
        </span>

        <validation-observer tag="form" ref="observer" v-show="showInputs"
          @submit.prevent="handleForward()">
          <div class="w-full flex flex-col space-y-2">

            <validation-provider class="w-full" name="nome" rules="required" v-slot="{ errors }">
              <div class="w-full flex flex-col space-y-2">
                <label for="name" class="nxp-label">
                  Nome de quem te indicou
                </label>
                <vs-input id="name" class="w-full" v-model="name" type="text" :disabled="alreadyReferral"
                  :danger="errors.length > 0" />
                <span class="text-danger text-sm">
                  {{ errors[0] }}
                </span>
              </div>
            </validation-provider>
            <validation-provider class="w-full" name="email" rules="required|email" v-slot="{ errors }">
              <div class="w-full flex flex-col space-y-2">
                <label for="email" class="nxp-label">
                  Email de quem te indicou
                </label>
                <vs-input id="email" class="w-full" v-model="email" type="email" :disabled="alreadyReferral"
                  :danger="errors.length > 0" />
                <span class="text-danger text-sm">
                  {{ errors[0] }}
                </span>
              </div>
            </validation-provider>

          </div>
        </validation-observer>
      </div>

      <button class="nxp-continue-btn mt-16" 
        :style="{ background: color }" 
        :disabled="showInputs && invalid"
        @click="handleForward">Continuar</button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/scss/talent/views/steps/step.scss";

.anonimous__msg {
  margin-top: 1rem;
  font-size: 0.875rem;
  color: #ff0000;

  &::before {
    content: "* ";
  }
}

.container {
  margin: 2.0625rem 0;


  &__radio-group {
    label:first-child {
      margin-top: 1rem;
      margin-right: 2rem;
    }
  }

  &__form-group {
    width: 100%;
    display: flex;
    align-items: center;
    margin-top: 1rem;

    span:first-child {
      margin-right: 1rem;
    }

    @media (max-width: 768px) {
      flex-wrap: wrap;

      span:first-child {
        margin-bottom: 1rem;
        margin-right: 0;
      }
    }
  }
}

.button-group {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  margin-top: 2rem;
}
</style>
